// Form validation utilities for advanced recommendation form

export interface ValidationError {
  field: string;
  message: string;
  section?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export interface MandatoryValidationResult {
  isValid: boolean;
  missingFields: string[];
  missingCount: number;
  filledCount: number;
}

// ===== PHASE 1: MANDATORY FIELDS =====

// List of 17 mandatory fields that must be filled before submission
export const MANDATORY_FIELDS = [
  'handicap',
  'age',
  'height_inches',
  'years_playing',
  'dominant_hand',
  'driver_swing_data.swing_speed_driver',
  'driver_swing_data.attack_angle_driver',
  'launch_monitor_driver.launch_angle_driver',
  'launch_monitor_driver.spin_rate_driver',
  'launch_monitor_driver.ball_speed_driver',
  'launch_monitor_iron.launch_angle_7i',
  'launch_monitor_iron.spin_rate_7i',
  'launch_monitor_iron.ball_speed_7i',
  'equipment_history.current_clubs_driver.shaft_flex',
  'equipment_history.current_clubs_irons.shaft_flex',
  'goals_challenges.primary_goal',
  'goals_challenges.biggest_frustration'
] as const;

// User-friendly display names for mandatory fields
export const MANDATORY_FIELD_LABELS: Record<string, string> = {
  'handicap': 'Handicap',
  'age': 'Age',
  'height_inches': 'Height',
  'years_playing': 'Years Playing Golf',
  'dominant_hand': 'Dominant Hand',
  'driver_swing_data.swing_speed_driver': 'Driver Swing Speed',
  'driver_swing_data.attack_angle_driver': 'Driver Attack Angle',
  'launch_monitor_driver.launch_angle_driver': 'Driver Launch Angle',
  'launch_monitor_driver.spin_rate_driver': 'Driver Spin Rate',
  'launch_monitor_driver.ball_speed_driver': 'Driver Ball Speed',
  'launch_monitor_iron.launch_angle_7i': '7-Iron Launch Angle',
  'launch_monitor_iron.spin_rate_7i': '7-Iron Spin Rate',
  'launch_monitor_iron.ball_speed_7i': '7-Iron Ball Speed',
  'equipment_history.current_clubs_driver.shaft_flex': 'Driver Shaft Flex',
  'equipment_history.current_clubs_irons.shaft_flex': 'Iron Shaft Flex',
  'goals_challenges.primary_goal': 'Primary Goal',
  'goals_challenges.biggest_frustration': 'Biggest Frustration'
};

// Allowed values for dropdown fields
export const ALLOWED_VALUES = {
  dominant_hand: ['Right', 'Left'],
  gender: ['Male', 'Female', 'Other', 'Prefer not to say'],
  shaft_flex: ['Ladies', 'Senior', 'Regular', 'Stiff', 'Extra Stiff', 'Wedge'],
  dispersion_pattern_driver: ['tight', 'moderate', 'wide'],
  dispersion_pattern_irons: ['tight', 'moderate', 'wide'],
  dispersion_pattern_wedges: ['tight', 'moderate', 'wide'],
  preferred_trajectory_irons: ['penetrating', 'medium', 'high'],
  preferred_trajectory_wedges: ['penetrating', 'medium', 'high'],
  primary_goal: ['more_accuracy', 'more_distance', 'consistent_distance', 'other'],
  biggest_frustration: ['inconsistent', 'distance', 'inaccurate', 'mental'],
  shot_shape_tendency: ['strong_draw', 'slight_draw', 'straight', 'slight_fade', 'strong_fade'],
  turf_interaction: ['heavy_divot', 'medium_divot', 'light_brush', 'clean_contact'],
  typical_miss: ['left', 'right', 'short', 'long', 'thin', 'fat']
} as const;

/**
 * Validate that all mandatory fields are filled
 * Returns list of missing fields and validation status
 *
 * NOTE: Ball flight data can be in TWO locations:
 * - launch_monitor_driver/iron (when user has launch monitor)
 * - driver_ball_flight/iron_data (when user enters manually)
 * Validation checks BOTH locations to handle either case.
 */
export const validateMandatoryFields = (formData: any): MandatoryValidationResult => {
  const missingFields: string[] = [];

  console.log('🔍 VALIDATION DEBUG - Starting validation');
  console.log('FormData:', JSON.stringify(formData, null, 2));

  for (const fieldPath of MANDATORY_FIELDS) {
    let value = getNestedValue(formData, fieldPath);

    // Special handling for fields that can be in multiple locations

    // 1. Ball flight fields: launch_monitor_* OR driver_ball_flight/iron_data/wedge_data
    if ((value === undefined || value === null || value === '') && fieldPath.includes('launch_monitor')) {
      const alternatePath = fieldPath
        .replace('launch_monitor_driver', 'driver_ball_flight')
        .replace('launch_monitor_iron', 'iron_data')
        .replace('launch_monitor_wedge', 'wedge_data');

      if (alternatePath !== fieldPath) {
        const altValue = getNestedValue(formData, alternatePath);
        console.log(`  📍 ${fieldPath}: primary empty, checking alternate ${alternatePath} = ${altValue}`);
        value = altValue;
      }
    }

    // 2. Swing data fields: driver_swing_data.* OR top-level flat field
    // Backend prefill returns flat fields, form submits nested
    if ((value === undefined || value === null || value === '') && fieldPath.includes('driver_swing_data.')) {
      const flatFieldName = fieldPath.replace('driver_swing_data.', '');
      const altValue = formData[flatFieldName];
      console.log(`  📍 ${fieldPath}: nested empty, checking flat field ${flatFieldName} = ${altValue}`);
      value = altValue;
    }

    // Check if value is empty (undefined, null, empty string, or NaN)
    if (value === undefined || value === null || value === '' ||
        (typeof value === 'number' && isNaN(value))) {
      console.log(`  ❌ MISSING: ${fieldPath} (value: ${value})`);
      missingFields.push(fieldPath);
    } else {
      console.log(`  ✅ VALID: ${fieldPath} = ${value}`);
    }
  }

  const filledCount = MANDATORY_FIELDS.length - missingFields.length;

  console.log('📊 VALIDATION RESULT:', { filledCount, missingCount: missingFields.length, missingFields });

  return {
    isValid: missingFields.length === 0,
    missingFields,
    missingCount: missingFields.length,
    filledCount
  };
};

/**
 * Helper function to get nested object values using dot notation
 * e.g., getNestedValue(obj, 'current_clubs_driver.shaft_flex')
 */
const getNestedValue = (obj: any, path: string): any => {
  if (!obj) return undefined;

  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === undefined || current === null) {
      return undefined;
    }
    current = current[part];
  }

  return current;
};

/**
 * Validate dropdown field has an allowed value
 */
export const validateDropdownValue = (
  value: string | undefined,
  fieldName: keyof typeof ALLOWED_VALUES,
  displayName?: string
): ValidationError | null => {
  if (!value) return null; // Empty is OK unless it's a required field

  const allowedValues = ALLOWED_VALUES[fieldName];
  if (!allowedValues) return null;

  // Convert readonly array to regular array for includes check
  const allowedValuesArray = Array.from(allowedValues) as string[];
  if (!allowedValuesArray.includes(value)) {
    return {
      field: fieldName,
      message: `${displayName || fieldName} must be one of: ${allowedValuesArray.join(', ')}`
    };
  }

  return null;
};

// Range validation helper
export const validateRange = (
  value: number | undefined,
  min: number,
  max: number,
  fieldName: string,
  section?: string
): ValidationError | null => {
  if (value === undefined || value === null) return null;
  
  if (value < min || value > max) {
    return {
      field: fieldName,
      message: `${fieldName} must be between ${min} and ${max}`,
      section
    };
  }
  return null;
};

// Email validation
export const validateEmail = (email: string | undefined): ValidationError | null => {
  if (!email) return null;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      field: 'user_email',
      message: 'Please enter a valid email address'
    };
  }
  return null;
};

// Required field validation
export const validateRequired = (
  value: any,
  fieldName: string,
  section?: string
): ValidationError | null => {
  if (value === undefined || value === null || value === '') {
    return {
      field: fieldName,
      message: `${fieldName} is required`,
      section
    };
  }
  return null;
};

// Validate basic information section
export const validateBasicInfo = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  // Handicap validation
  const handicapError = validateRange(data.handicap, -5, 40, 'Handicap', 'Basic Information');
  if (handicapError) errors.push(handicapError);
  
  // Age validation
  const ageError = validateRange(data.age, 10, 100, 'Age', 'Basic Information');
  if (ageError) errors.push(ageError);
  
  // Height validation
  const heightError = validateRange(data.height_inches, 48, 84, 'Height', 'Basic Information');
  if (heightError) errors.push(heightError);
  
  // Years playing validation
  const yearsError = validateRange(data.years_playing, 0, 80, 'Years Playing', 'Basic Information');
  if (yearsError) errors.push(yearsError);
  
  // Email validation
  const emailError = validateEmail(data.user_email);
  if (emailError) errors.push(emailError);
  
  return errors;
};

// Validate swing characteristics
export const validateSwingCharacteristics = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Driver swing speed - check both locations
  const driverSpeedError = validateRange(
    data.driver_swing_data?.swing_speed_driver,
    50,
    150,
    'Driver Swing Speed',
    'Swing Characteristics'
  );
  if (driverSpeedError) errors.push(driverSpeedError);

  // 6-iron/7-iron swing speed - check both locations
  const ironSpeedError = validateRange(
    data.iron_swing_data?.swing_speed_6iron || data.iron_swing_data?.swing_speed_7iron,
    40,
    120,
    'Iron Swing Speed',
    'Swing Characteristics'
  );
  if (ironSpeedError) errors.push(ironSpeedError);

  // Attack angles - check both locations
  const driverAttackError = validateRange(
    data.driver_swing_data?.attack_angle_driver,
    -10,
    10,
    'Driver Attack Angle',
    'Swing Characteristics'
  );
  if (driverAttackError) errors.push(driverAttackError);

  const ironAttackError = validateRange(
    data.iron_swing_data?.attack_angle_irons,
    -10,
    5,
    'Iron Attack Angle',
    'Swing Characteristics'
  );
  if (ironAttackError) errors.push(ironAttackError);

  return errors;
};

// Validate ball flight data
export const validateBallFlightData = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  // Driver ball flight validation
  if (data.launch_monitor_driver) {
    const driver = data.launch_monitor_driver;

    const launchAngleError = validateRange(
      driver.launch_angle_driver,
      4,
      25,
      'Driver Launch Angle',
      'Ball Flight Data'
    );
    if (launchAngleError) errors.push(launchAngleError);

    const spinRateError = validateRange(
      driver.spin_rate_driver,
      1000,
      5000,
      'Driver Spin Rate',
      'Ball Flight Data'
    );
    if (spinRateError) errors.push(spinRateError);

    const ballSpeedError = validateRange(
      driver.ball_speed_driver,
      60,
      220,
      'Driver Ball Speed',
      'Ball Flight Data'
    );
    if (ballSpeedError) errors.push(ballSpeedError);

    const carryError = validateRange(
      driver.driver_carry_distance || driver.carry_distance,
      150,
      350,
      'Driver Carry Distance',
      'Ball Flight Data'
    );
    if (carryError) errors.push(carryError);

  }
  
  // 7-Iron ball flight validation
  if (data.launch_monitor_iron) {
    const iron = data.launch_monitor_iron;

    const launchAngleError = validateRange(
      iron.launch_angle_7i,
      8,
      25,
      '7-Iron Launch Angle',
      'Ball Flight Data'
    );
    if (launchAngleError) errors.push(launchAngleError);

    const spinRateError = validateRange(
      iron.spin_rate_7i,
      2000,
      10000,
      '7-Iron Spin Rate',
      'Ball Flight Data'
    );
    if (spinRateError) errors.push(spinRateError);

    const ballSpeedError = validateRange(
      iron.ball_speed_7i,
      60,
      150,
      '7-Iron Ball Speed',
      'Ball Flight Data'
    );
    if (ballSpeedError) errors.push(ballSpeedError);

    const carryError = validateRange(
      iron.iron_carry_distance || iron.carry_distance_7i,
      100,
      200,
      '7-Iron Carry Distance',
      'Ball Flight Data'
    );
    if (carryError) errors.push(carryError);
  }
  
  // Wedge ball flight validation
  if (data.launch_monitor_wedge) {
    const wedge = data.launch_monitor_wedge;

    const launchAngleError = validateRange(
      wedge.launch_angle_wedge1 || wedge.launch_angle_wedge,
      15,
      50,
      'Wedge Launch Angle',
      'Ball Flight Data'
    );
    if (launchAngleError) errors.push(launchAngleError);

    const spinRateError = validateRange(
      wedge.spin_rate_wedge1 || wedge.spin_rate_wedge,
      6000,
      12000,
      'Wedge Spin Rate',
      'Ball Flight Data'
    );
    if (spinRateError) errors.push(spinRateError);

    const carryError = validateRange(
      wedge.wedge_carry_distance || wedge.carry_distance_wedge || wedge.carry_distance_wedge1,
      50,
      180,
      'Wedge Carry Distance',
      'Ball Flight Data'
    );
    if (carryError) errors.push(carryError);
  }
  
  return errors;
};

// Validate physical profile
export const validatePhysicalProfile = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (data.physical_profile) {
    const physical = data.physical_profile;
    
    const wristToFloorError = validateRange(
      physical.wrist_to_floor, 
      25, 
      40, 
      'Wrist to Floor Measurement', 
      'Physical Profile'
    );
    if (wristToFloorError) errors.push(wristToFloorError);
  }
  
  return errors;
};

// Validate equipment history
export const validateEquipmentHistory = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (data.equipment_history) {
    const equipment = data.equipment_history;
    
    // Satisfaction ratings (1-10)
    const driverSatisfactionError = validateRange(
      equipment.current_driver_satisfaction, 
      1, 
      10, 
      'Driver Satisfaction', 
      'Equipment History'
    );
    if (driverSatisfactionError) errors.push(driverSatisfactionError);
    
    const ironSatisfactionError = validateRange(
      equipment.current_iron_satisfaction, 
      1, 
      10, 
      'Iron Satisfaction', 
      'Equipment History'
    );
    if (ironSatisfactionError) errors.push(ironSatisfactionError);
    
    const wedgeSatisfactionError = validateRange(
      equipment.current_wedge_satisfaction, 
      1, 
      10, 
      'Wedge Satisfaction', 
      'Equipment History'
    );
    if (wedgeSatisfactionError) errors.push(wedgeSatisfactionError);
    
    // Validate older brands arrays (max 5 items)
    if (equipment.older_brands_used_driver && equipment.older_brands_used_driver.length > 5) {
      errors.push({
        field: 'older_brands_used_driver',
        message: 'Maximum 5 previously used driver brands allowed',
        section: 'Equipment History'
      });
    }
    
    if (equipment.older_brands_used_irons && equipment.older_brands_used_irons.length > 5) {
      errors.push({
        field: 'older_brands_used_irons',
        message: 'Maximum 5 previously used iron brands allowed',
        section: 'Equipment History'
      });
    }
    
    if (equipment.older_brands_used_wedges && equipment.older_brands_used_wedges.length > 5) {
      errors.push({
        field: 'older_brands_used_wedges',
        message: 'Maximum 5 previously used wedge brands allowed',
        section: 'Equipment History'
      });
    }
  }
  
  return errors;
};

// Validate playing profile
export const validatePlayingProfile = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (data.playing_profile) {
    const playing = data.playing_profile;
    
    const roundsError = validateRange(
      playing.rounds_per_year, 
      0, 
      200, 
      'Rounds Per Year', 
      'Playing Profile'
    );
    if (roundsError) errors.push(roundsError);
    
    // Pressure performance ratings (1-10)
    const driverPressureError = validateRange(
      playing.driver_under_pressure, 
      1, 
      10, 
      'Driver Performance Under Pressure', 
      'Playing Profile'
    );
    if (driverPressureError) errors.push(driverPressureError);
    
    const ironPressureError = validateRange(
      playing.irons_under_pressure, 
      1, 
      10, 
      'Iron Performance Under Pressure', 
      'Playing Profile'
    );
    if (ironPressureError) errors.push(ironPressureError);
    
    const wedgePressureError = validateRange(
      playing.wedges_under_pressure, 
      1, 
      10, 
      'Wedge Performance Under Pressure', 
      'Playing Profile'
    );
    if (wedgePressureError) errors.push(wedgePressureError);
  }
  
  return errors;
};

// Validate goals and challenges
export const validateGoalsAndChallenges = (data: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (data.goals_challenges) {
    const goals = data.goals_challenges;
    
    const targetHandicapError = validateRange(
      goals.target_handicap, 
      -5, 
      40, 
      'Target Handicap', 
      'Goals & Challenges'
    );
    if (targetHandicapError) errors.push(targetHandicapError);
  }
  
  return errors;
};

// Master validation function
export const validateFormData = (data: any): ValidationResult => {
  const errors: ValidationError[] = [];
  
  // Validate all sections
  errors.push(...validateBasicInfo(data));
  errors.push(...validateSwingCharacteristics(data));
  errors.push(...validateBallFlightData(data));
  errors.push(...validatePhysicalProfile(data));
  errors.push(...validateEquipmentHistory(data));
  errors.push(...validatePlayingProfile(data));
  errors.push(...validateGoalsAndChallenges(data));
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get errors for a specific section
export const getErrorsForSection = (errors: ValidationError[], section: string): ValidationError[] => {
  return errors.filter(error => error.section === section);
};

// Get errors for a specific field
export const getErrorsForField = (errors: ValidationError[], field: string): ValidationError[] => {
  return errors.filter(error => error.field === field);
};